/**
 * @component ApplicationRow
 * @description A centralized component for displaying application rows across different dashboards.
 * This component handles both mobile and desktop views, providing a consistent interface for
 * displaying application information and actions.
 *
 * @example
 * Basic usage
 * <ApplicationRow
 *   application={applicationData}
 *   actions={actionButtons}
 *   isMobile={false}
 * />
 *
 * @example
 *  With all optional props
 * <ApplicationRow
 *   application={applicationData}
 *   actions={actionButtons}
 *   isMobile={isMobile}
 *   assigneeInfo="John Doe"
 *   additionalInfo={<CustomComponent />}
 * />
 *
 * @typedef {Object} Action
 * @property {React.ComponentType} icon - Lucide icon component to display
 * @property {string} label - Tooltip text for the action button
 * @property {Function} onClick - Click handler for the action
 * @property {string} [variant="outline"] - Button variant (outline, ghost, etc.)
 * @property {string} [className] - Additional CSS classes for styling
 *
 * @typedef {Object} Application
 * @property {string} applicationNumber - Unique identifier for the application
 * @property {string} applicationType - Type of application (e.g., "Chainsaw Registration")
 * @property {string} [requestType] - Type of request (optional)
 * @property {string} dateOfSubmission - ISO date string of submission
 * @property {string} status - Current status of the application
 *
 * @param {Object} props - Component props
 * @param {Application} props.application - Application data object
 * @param {Action[]} props.actions - Array of action objects for the buttons
 * @param {boolean} props.isMobile - Whether to render mobile or desktop view
 * @param {string|React.ReactNode} [props.assigneeInfo] - Optional assignee information
 * @param {React.ReactNode} [props.additionalInfo] - Optional additional information
 * @param {boolean} [props.showActionLabels] - Whether to show action labels
 *
 * @returns {React.ReactElement} Rendered application row component
 *
 * @note Status Variants
 * The component automatically maps status strings to appropriate badge styles:
 * - submitted → blue
 * - in progress → yellow
 * - returned → yellow (warning)
 * - accepted/approved → green
 * - released → green
 * - default → gray
 *
 * @note Mobile vs Desktop
 * - Mobile: Renders as a Card component with stacked layout
 * - Desktop: Renders as a TableRow with columns
 *
 * @note Column Widths (Desktop)
 * - Application Number: 20%
 * - Application Type: 20%
 * - Request Type: 15%
 * - Date: 15%
 * - Status: 15%
 * - Actions: 15%
 *
 * @example
 * // Example of creating action buttons
 * const actions = [
 *   {
 *     icon: Eye,
 *     label: "View Application",
 *     onClick: () => handleView(id),
 *     variant: "outline"
 *   },
 *   {
 *     icon: FileText,
 *     label: "View Certificate",
 *     onClick: () => handleCertificate(id),
 *     variant: "outline",
 *     className: "text-purple-600"
 *   }
 * ];
 *
 * @example
 * // Example with assignee info
 * <ApplicationRow
 *   application={app}
 *   actions={actions}
 *   isMobile={isMobile}
 *   assigneeInfo={`Assigned to: ${app.assignedTo}`}
 * />
 *
 * @example
 * // Example with additional info
 * <ApplicationRow
 *   application={app}
 *   actions={actions}
 *   isMobile={isMobile}
 *   additionalInfo={
 *     <div className="text-sm text-muted-foreground">
 *       Priority: {app.priority}
 *     </div>
 *   }
 * />
 *
 * @note Accessibility
 * - All action buttons have tooltips for better UX
 * - Buttons use proper ARIA labels
 * - Table structure is semantic for screen readers
 *
 * @note Performance
 * - Component uses React.memo internally for action buttons
 * - Date formatting is handled efficiently
 * - Status variant mapping is memoized
 *
 * @note Styling
 * - Uses Tailwind CSS for responsive design
 * - Consistent with shadcn/ui component library
 * - Custom color schemes for status badges
 * - Responsive spacing and layout
 *
 * @note Error Handling
 * - Safely handles missing requestType
 * - Validates date format
 * - Provides fallback for unknown status types
 *
 * @note Integration Points
 * Used in:
 * - ChiefDashboard
 * - TechnicalStaffDashboard
 * - PENRCENROfficerDashboard
 * - ReceivingReleasingClerkDashboard
 * - BillCollectorDashboard
 *
 * @note State Management
 * - Component is stateless
 * - All interactions are handled through props
 * - Parent components control all data flow
 *
 * @note Customization
 * The component can be customized through:
 * - Action button configurations
 * - Additional info content
 * - Assignee info display
 * - Status badge variants
 * - Mobile/desktop layouts
 *
 * @note Dependencies
 * Required packages:
 * - @/components/ui/badge
 * - @/components/ui/button
 * - @/components/ui/card
 * - @/components/ui/table
 * - @/components/ui/tooltip
 * - date-fns (for date formatting)
 * - lucide-react (for icons)
 */

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { TableCell, TableRow } from "@/components/ui/table";
import {
   Tooltip,
   TooltipContent,
   TooltipProvider,
   TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { format } from 'date-fns';
import React from 'react';

/**
 * A centralized application row component that can be used across different dashboards
 * @param {Object} props - Component props
 * @param {Object} props.application - The application data
 * @param {Array} props.actions - Array of action objects with icon, label, onClick, variant, and className
 * @param {boolean} props.isMobile - Whether the component is being rendered on mobile
 * @param {Object} props.assigneeInfo - Optional assignee information to display
 * @param {Object} props.additionalInfo - Optional additional information to display
 * @param {boolean} [props.showActionLabels] - Whether to show action labels
 */
const ApplicationRow = ({
   application,
   actions,
   isMobile,
   assigneeInfo,
   additionalInfo,
   showActionLabels = false,
}) => {
   // Render assignee information if provided
   const renderAssigneeInfo = () => {
      if (!assigneeInfo) return null;

      return (
         <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <span className="font-medium">Assigned to:</span>
            <span>{assigneeInfo}</span>
         </div>
      );
   };

   // Render action buttons with tooltips
   const renderActionButtons = () => {
      return actions.map((action, index) => {
         return (
            <TooltipProvider key={index} delayDuration={200}>
               <Tooltip>
                  <TooltipTrigger asChild>
                     <Button
                        variant={action.variant || "outline"}
                        size={showActionLabels ? "sm" : "icon"}
                        onClick={action.onClick}
                        className={cn(
                           action.className,
                           showActionLabels
                              ? "gap-2 px-3 min-w-[90px] items-center flex"
                              : "w-9 h-9 p-0 flex items-center justify-center"
                        )}
                     >
                        <action.icon className="h-4 w-4 shrink-0" />
                        {showActionLabels && (
                           <span className="truncate max-w-[100px]">{action.label}</span>
                        )}
                     </Button>
                  </TooltipTrigger>
                  {!showActionLabels && (
                     <TooltipContent>
                        <p>{action.label} {action.hotkey ? `(${action.hotkey})` : ''}</p>
                     </TooltipContent>
                  )}
               </Tooltip>
            </TooltipProvider>
         );
      });
   };

   // Mobile view
   if (isMobile) {
      return (
         <Card className="p-4 space-y-3">
            <div className="flex justify-between items-start">
               <div>
                  <p className="font-medium">{application.applicationNumber}</p>
                  <p className="text-sm text-muted-foreground">{application.applicationType}</p>
                  <p className="text-sm text-muted-foreground">{application.requestType || "N/A"}</p>
                  {renderAssigneeInfo()}
                  {additionalInfo}
               </div>
               <Badge
                  variant={getStatusVariant(application.status).variant}
                  className={getStatusVariant(application.status).className}
               >
                  {application.status}
               </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
               {format(new Date(application.dateOfSubmission), 'MMM d, yyyy')}
            </p>
            <div className="flex flex-wrap gap-2 justify-end">
               {renderActionButtons()}
            </div>
         </Card>
      );
   }

   // Desktop view
   return (
      <TableRow>
         <TableCell className="w-[20%]">
            <div className="space-y-1">
               <span>{application.applicationNumber}</span>
               {renderAssigneeInfo()}
               {additionalInfo}
            </div>
         </TableCell>
         <TableCell className="w-[20%] text-center">{application.applicationType}</TableCell>
         <TableCell className="w-[15%] text-center">{application.requestType || "N/A"}</TableCell>
         <TableCell className="w-[15%] text-center">
            {format(new Date(application.dateOfSubmission), 'MMM d, yyyy')}
         </TableCell>
         <TableCell className="w-[15%]">
            <div className="flex justify-center">
               <Badge
                  variant={getStatusVariant(application.status).variant}
                  className={getStatusVariant(application.status).className}
               >
                  {application.status}
               </Badge>
            </div>
         </TableCell>
         <TableCell className={cn(
            "text-center",
            showActionLabels ? "w-[25%]" : "w-[15%]"
         )}>
            <div className={cn(
               "flex justify-center items-center",
               showActionLabels ? "gap-3" : "gap-2"
            )}>
               {renderActionButtons()}
            </div>
         </TableCell>
      </TableRow>
   );
};

// Helper function to map status to badge variant and custom colors
const getStatusVariant = (status) => {
   switch (status.toLowerCase()) {
      case 'submitted':
         return {
            variant: 'default',
            className: 'bg-blue-100 text-blue-800 hover:bg-blue-100/80'
         };
      case 'in progress':
         return {
            variant: 'secondary',
            className: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100/80'
         };
      case 'returned':
         return {
            variant: 'warning',
            className: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100/80'
         };
      case 'accepted':
      case 'approved':
         return {
            variant: 'success',
            className: 'bg-green-100 text-green-800 hover:bg-green-100/80'
         };
      case 'released':
         return {
            variant: 'success',
            className: 'bg-green-100 text-green-800 hover:bg-green-100/80'
         };
      default:
         return {
            variant: 'secondary',
            className: 'bg-gray-100 text-gray-800 hover:bg-gray-100/80'
         };
   }
};

export default ApplicationRow;